import React, { useMemo } from 'react';
import {
  Autocomplete,
  AutocompleteValue,
  Box,
  FilterOptionsState,
  SlotProps,
  TextFieldOwnerState,
} from '@mui/material';
import { TextInput } from '../TextField/TextInput';
import { IAutocompleteFieldProps, TAutocompleteOption } from './types';
import { muiFilter } from '~/components/FormComponents/utils.ts';

export const AutocompleteField = <
  T extends TAutocompleteOption,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
>({
  options,
  value,
  onChange,
  onBlur,
  label,
  error,
  helperText,
  variant,
  noOptionsText = 'Така опція відсутня',
  inputRef,
  inputValue,
  inputSlotProps,
  preserveOptionsOrder = false,
  ...autocompleteProps
}: IAutocompleteFieldProps<T, Multiple, DisableClearable, FreeSolo>) => {
  const fieldName =
    (
      inputSlotProps?.htmlInput as SlotProps<'input', {}, TextFieldOwnerState> & {
        'data-testid': string;
      }
    )?.['data-testid'] ?? '';

  const sortedOptions = useMemo(
    () =>
      preserveOptionsOrder
        ? options ?? []
        : [...(options ?? [])].sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', 'uk')),
    [options, preserveOptionsOrder]
  );

  return (
    <Autocomplete
      options={sortedOptions}
      filterOptions={(opts, state) =>
        muiFilter(opts, state as FilterOptionsState<TAutocompleteOption>) as T[]
      }
      inputValue={inputValue}
      value={value as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>}
      onChange={(_, newValue) => {
        onChange(newValue as Multiple extends true ? T[] : T | null);
      }}
      onBlur={onBlur}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        if (option && typeof option === 'object' && 'label' in option) {
          return option.label ?? '';
        }
        return '';
      }}
      isOptionEqualToValue={(option, value) =>
        option.value === ((value as TAutocompleteOption)?.value ?? value)
      }
      noOptionsText={noOptionsText}
      renderOption={(props, option, { index }) => {
        const { key, ...restProps } = props;
        return (
          <Box
            component="li"
            key={`${option.value}-${index}`}
            {...restProps}
            data-testid={`option-${option?.label}`}
          >
            {option?.label}
          </Box>
        );
      }}
      {...autocompleteProps}
      slotProps={{
        ...autocompleteProps.slotProps,
        clearIndicator: {
          ...((autocompleteProps.slotProps?.clearIndicator as object) ?? {}),
          'data-testid': `${fieldName}-clear-btn`,
        } as React.ComponentPropsWithRef<'button'>,
        popupIndicator: {
          ...((autocompleteProps.slotProps?.popupIndicator as object) ?? {}),
          'data-testid': `${fieldName}-popup-btn`,
        } as React.ComponentPropsWithRef<'button'>,
      }}
      renderInput={(params) => {
        return (
          <TextInput
            {...params}
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            inputRef={inputRef}
            label={label}
            error={Boolean(error)}
            helperText={helperText}
            variant={variant}
            slotProps={{
              ...params.slotProps,
              ...inputSlotProps,
              htmlInput: {
                ...params.slotProps?.htmlInput,
                ...inputSlotProps?.htmlInput,
              },
            }}
          />
        );
      }}
    />
  );
};
